import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Button, Typography, Alert, Paper, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { supabase } from '../lib/supabase';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetError(t('auth.emailRequired'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setResetError(t('auth.invalidEmail'));
      return;
    }

    setResetError('');
    setResetMessage('');
    setResetLoading(true);

    try {
      // Send OTP for password reset
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail);

      if (error) throw error;

      setResetMessage(t('auth.otpSent'));
      
      // Navigate to reset password page with email after a short delay
      setTimeout(() => {
        setForgotPasswordOpen(false);
        navigate(`/reset-password?email=${encodeURIComponent(resetEmail)}`);
      }, 2000);
    } catch (err: any) {
      setResetError(err.message || t('auth.resetEmailError'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mx: 2 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="PCU Logo"
            sx={{ width: 80, height: 80, objectFit: 'contain', display: 'block', mx: 'auto' }}
          />
        </Box>

        <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
          {t('adminLogin')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            autoComplete="email"
          />

          <TextField
            fullWidth
            label={t('password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            margin="normal"
            autoComplete="current-password"
          />

          <Box sx={{ mt: 1, mb: 2, textAlign: 'end' }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setForgotPasswordOpen(true)}
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              {t('auth.forgotPassword')}
            </Link>
          </Box>

          <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ mt: 2 }}>
            {loading ? t('signingIn') : t('signIn')}
          </Button>
        </Box>
      </Paper>

      {/* Forgot Password Dialog - OTP Based */}
      <Dialog open={forgotPasswordOpen} onClose={() => !resetLoading && setForgotPasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('auth.forgotPassword')}
          <IconButton
            aria-label="close"
            onClick={() => setForgotPasswordOpen(false)}
            sx={{
              position: 'absolute',
              insetInlineEnd: 8,
              top: 8,
            }}
            disabled={resetLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {resetMessage && <Alert severity="success" sx={{ mb: 2 }}>{resetMessage}</Alert>}
          {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.otpInstructions')}
          </Typography>

          <TextField
            fullWidth
            label={t('email')}
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            margin="normal"
            autoFocus
            disabled={resetLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotPasswordOpen(false)} disabled={resetLoading}>
            {t('dialog.cancel')}
          </Button>
          <Button onClick={handleForgotPassword} variant="contained" disabled={resetLoading}>
            {resetLoading ? t('common.loading') : t('auth.sendOtp')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
